import type { EquityPerformance } from '#/api/generated/fINRAMarketDataAPI.schemas';
import { EquityChartSection } from '#/components/charts/EquityChartSection';
import { DefinitionTooltip } from '#/components/content/DefinitionTooltip';
import { DisclosureStatementButton } from '#/components/content/DisclosureStatement';
import { RangeBar } from '#/components/ui/RangeBar';
import type { RangeSegment } from '#/components/ui/RangeSegments';
import { RangeSegments } from '#/components/ui/RangeSegments';
import { SectionHeader } from '#/components/ui/SectionHeader';
import { useDefinitionDrawer } from '#/context/DefinitionDrawerContext';
import { MARKET_CAP_CATEGORY_RANGE_LABEL } from '#/lib/market-cap-category';
import { DASH } from '#/utils/constants';
import {
  formatCompact,
  formatCompactCurrency,
  formatCurrency,
  formatDecimal,
  formatPercent,
} from '#/utils/format';

import { StatGroup, StatRow, valueClass } from './StatGroup';

const MARKET_CAP_SEGMENTS: RangeSegment[] = [
  { label: '$0', min: null, max: 250e6 },
  { min: 250e6, max: 2e9 },
  { min: 2e9, max: 10e9 },
  { min: 10e9, max: 200e9 },
  { label: '$200B+', min: 200e9, max: null },
];

const BETA_SEGMENTS: RangeSegment[] = [
  {
    label: '< -1',
    min: null,
    max: -1,
    description: 'Moves inversely and more extremely than the market',
  },
  {
    label: '-1 to 0',
    min: -1,
    max: 0,
    description: 'Moves inversely to the market',
  },
  {
    label: '0 to 1',
    min: 0,
    max: 1,
    // `1` lands here (at the top), not in the `> 1` segment.
    maxInclusive: true,
    description: (value) =>
      value === 1
        ? 'As volatile as the market'
        : 'Less volatile than the market',
  },
  {
    label: '> 1',
    min: 1,
    max: null,
    description: 'More volatile than the market',
  },
];

function buildMarketCapSegmentTooltip(
  data: EquityPerformance,
): { termSlug: string; label: string; definition?: string } | undefined {
  if (!data.market_cap_category) {
    return undefined;
  }
  return {
    termSlug: data.market_cap_category.toLowerCase().replace(/\s+/g, '-'),
    label: data.market_cap_category,
    definition: MARKET_CAP_CATEGORY_RANGE_LABEL[data.market_cap_category],
  };
}

interface EquityPerformanceSectionProps {
  symbol: string;
  data: EquityPerformance | null | undefined;
  date?: string | null;
  description?: string | null;
  priceIntro?: string | null;
  riskIntro?: string | null;
}

export function EquityPerformanceSection({
  symbol,
  data,
  date,
  description,
  priceIntro,
  riskIntro,
}: EquityPerformanceSectionProps) {
  const { termDescription } = useDefinitionDrawer();

  if (!data) {
    return null;
  }
  return (
    <section
      id="performance"
      className="flow flow-32 scroll-mt-(--scroll-offset)"
    >
      <SectionHeader
        title="Performance"
        date={date}
        description={description}
        actions={<DisclosureStatementButton />}
      />

      <div className="-mx-gap sm:border-divider-subtle divide-divider-subtle divide-y sm:mx-0 sm:rounded sm:border">
        <EquityChartSection
          symbol={symbol}
          previousClose={data.previous_close}
        />

        <StatGroup id="key-stats" title="Key Stats">
          <StatRow
            label={
              <DefinitionTooltip termSlug="market-cap">
                Market Cap
              </DefinitionTooltip>
            }
            description={termDescription('market-cap')}
            value={
              data.market_cap != null ? (
                <RangeSegments
                  className={valueClass}
                  value={data.market_cap}
                  segments={MARKET_CAP_SEGMENTS}
                  format={formatCompactCurrency}
                  tooltip={buildMarketCapSegmentTooltip(data)}
                />
              ) : undefined
            }
          />
          <StatRow
            label={
              <DefinitionTooltip termSlug="previous-close">
                Previous Close
              </DefinitionTooltip>
            }
            description={termDescription('previous-close')}
            value={formatCurrency(data.previous_close)}
          />
          <StatRow
            label={
              <DefinitionTooltip termSlug="5-year-range">
                5 Year Range
              </DefinitionTooltip>
            }
            description={termDescription('5-year-range')}
            value={
              <RangeBar
                className={valueClass}
                low={data.five_year_range?.low}
                high={data.five_year_range?.high}
                current={data.previous_close}
                label="5 year price range"
              />
            }
          />
          <StatRow
            label={
              <DefinitionTooltip termSlug="1-year-range">
                52-Week Range
              </DefinitionTooltip>
            }
            description={termDescription('1-year-range')}
            value={
              <RangeBar
                className={valueClass}
                low={data.fifty_two_week_range?.low}
                high={data.fifty_two_week_range?.high}
                current={data.previous_close}
                label="52-week price range"
              />
            }
          />
          <StatRow
            label={
              <DefinitionTooltip termSlug="day-range">
                Day Range
              </DefinitionTooltip>
            }
            description={termDescription('day-range')}
            value={
              <RangeBar
                className={valueClass}
                low={data.day_range?.low}
                high={data.day_range?.high}
                current={data.previous_close}
                label="Day price range"
              />
            }
          />
          <StatRow
            label={
              <DefinitionTooltip termSlug="bid-ask-spread">
                Bid / Ask / Spread
              </DefinitionTooltip>
            }
            description={termDescription('bid-ask-spread')}
            value={
              <>
                <span>{formatCurrency(data.bid) ?? DASH}</span>
                <span> / </span>
                <span>{formatCurrency(data.ask) ?? DASH}</span>
                <span> / </span>
                <span>{formatCurrency(data.spread) ?? DASH}</span>
              </>
            }
          />
          <StatRow
            label={
              <DefinitionTooltip termSlug="volume-avg">
                Volume / Avg
              </DefinitionTooltip>
            }
            description={termDescription('volume-avg')}
            value={
              <>
                <span>{formatCompact(data.volume) ?? DASH}</span>
                <span> / </span>
                <span>{formatCompact(data.average_volume) ?? DASH}</span>
              </>
            }
          />
          <StatRow
            label={
              <DefinitionTooltip termSlug="enterprise-valueebitda">
                Enterprise Value/EBITDA
              </DefinitionTooltip>
            }
            description={termDescription('enterprise-valueebitda')}
            value={formatDecimal(data.enterprise_value_ebitda)}
          />
        </StatGroup>

        <StatGroup
          id="price"
          title="Price"
          description={priceIntro ?? undefined}
        >
          <StatRow
            label={
              <DefinitionTooltip termSlug="dividend-yield-trailing">
                Dividend Yield (Trailing)
              </DefinitionTooltip>
            }
            description={termDescription('dividend-yield-trailing')}
            value={formatPercent(data.dividend_yield_trailing, {
              isRatio: true,
            })}
          />
          <StatRow
            label={
              <DefinitionTooltip termSlug="dividend-yield-forward">
                Dividend Yield (Forward)
              </DefinitionTooltip>
            }
            description={termDescription('dividend-yield-forward')}
            value={formatPercent(data.dividend_yield_forward, {
              isRatio: true,
            })}
          />
          <StatRow
            label={
              <DefinitionTooltip termSlug="total-yield">
                Total Yield
              </DefinitionTooltip>
            }
            description={termDescription('total-yield')}
            value={formatPercent(data.total_yield, { isRatio: true })}
          />
          <StatRow
            label={
              <DefinitionTooltip termSlug="price-earnings-pe-ratio">
                Price-to-Earnings (PE) Ratio
              </DefinitionTooltip>
            }
            description={termDescription('price-earnings-pe-ratio')}
            value={formatDecimal(data.pe_ratio)}
          />
          <StatRow
            label={
              <DefinitionTooltip termSlug="price-sales">
                Price to Sales
              </DefinitionTooltip>
            }
            description={termDescription('price-sales')}
            value={formatDecimal(data.price_to_sales)}
          />
        </StatGroup>

        <StatGroup id="risk" title="Risk" description={riskIntro ?? undefined}>
          <StatRow
            label={<DefinitionTooltip termSlug="beta">Beta</DefinitionTooltip>}
            description={termDescription('beta')}
            value={
              data.beta != null ? (
                <RangeSegments
                  className={valueClass}
                  value={data.beta}
                  segments={BETA_SEGMENTS}
                  // Truncate toward zero so a beta just under an integer
                  // (e.g. 0.998) doesn't round up across the segment boundary.
                  format={(value) =>
                    formatDecimal(value, { roundingMode: 'trunc' })
                  }
                />
              ) : undefined
            }
          />
          <StatRow
            label={
              <DefinitionTooltip termSlug="standard-deviation">
                Standard Deviation
              </DefinitionTooltip>
            }
            description={termDescription('standard-deviation')}
            value={formatDecimal(data.standard_deviation)}
          />
        </StatGroup>
      </div>
    </section>
  );
}
